import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { 
  QrCode, 
  Smartphone, 
  Globe, 
  Shield, 
  Zap, 
  Users, 
  BarChart3, 
  Palette,
  Download,
  Share2,
  FileText,
  CheckCircle
} from 'lucide-react';
import LandingPageLayout from '../components/LandingPageLayout';

const FeaturesPage: React.FC = () => {
  const { t } = useTranslation();

  // 페이지 로드 시 상단으로 스크롤
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: <QrCode className="h-8 w-8" />,
      title: t('features.qr.title', 'QR Code Generation'),
      description: t('features.qr.description', 'Generate professional QR codes for your business cards and profiles instantly.'),
      benefits: [
        t('features.qr.benefit1', 'Customizable QR designs'),
        t('features.qr.benefit2', 'High-resolution output'),
        t('features.qr.benefit3', 'Multiple format support')
      ]
    },
    {
      icon: <Smartphone className="h-8 w-8" />,
      title: t('features.mobile.title', 'Mobile-First Design'),
      description: t('features.mobile.description', 'Create and manage your digital business cards on any device with our responsive design.'),
      benefits: [
        t('features.mobile.benefit1', 'Cross-platform compatibility'),
        t('features.mobile.benefit2', 'Touch-optimized interface'),
        t('features.mobile.benefit3', 'Offline functionality')
      ]
    },
    {
      icon: <Globe className="h-8 w-8" />,
      title: t('features.web.title', 'Web Profile Pages'),
      description: t('features.web.description', 'Get a professional web profile page that showcases your business information beautifully.'),
      benefits: [
        t('features.web.benefit1', 'Custom domain support'),
        t('features.web.benefit2', 'SEO optimized'),
        t('features.web.benefit3', 'Social media integration')
      ]
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: t('features.security.title', 'Data Security'),
      description: t('features.security.description', 'Your data is protected with enterprise-grade security and privacy controls.'),
      benefits: [
        t('features.security.benefit1', 'End-to-end encryption'),
        t('features.security.benefit2', 'GDPR compliant'),
        t('features.security.benefit3', 'Regular backups')
      ]
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: t('features.analytics.title', 'Contact Analytics'),
      description: t('features.analytics.description', 'Track how your business card is being viewed and shared with detailed analytics.'),
      benefits: [
        t('features.analytics.benefit1', 'View statistics'),
        t('features.analytics.benefit2', 'Geographic insights'),
        t('features.analytics.benefit3', 'Engagement metrics')
      ]
    },
    {
      icon: <BarChart3 className="h-8 w-8" />,
      title: t('features.invoice.title', 'Invoice Management'),
      description: t('features.invoice.description', 'Create, send, and track professional invoices directly from your business profile.'),
      benefits: [
        t('features.invoice.benefit1', 'PDF generation'),
        t('features.invoice.benefit2', 'Payment tracking'),
        t('features.invoice.benefit3', 'Automated reminders')
      ]
    },
    {
      icon: <Palette className="h-8 w-8" />,
      title: t('features.customization.title', 'Customization'),
      description: t('features.customization.description', 'Personalize your business card with custom themes, colors, and layouts.'),
      benefits: [
        t('features.customization.benefit1', 'Multiple templates'),
        t('features.customization.benefit2', 'Brand colors'),
        t('features.customization.benefit3', 'Logo upload')
      ]
    }
  ];

  const integrations = [
    {
      icon: <Download className="h-6 w-6" />,
      name: t('features.integrations.vcard', 'vCard Download'),
      description: t('features.integrations.vcard.desc', 'One-click contact saving')
    },
    {
      icon: <Share2 className="h-6 w-6" />,
      name: t('features.integrations.share', 'Social Sharing'),
      description: t('features.integrations.share.desc', 'Share across all platforms')
    },
    {
      icon: <FileText className="h-6 w-6" />,
      name: t('features.integrations.pdf', 'PDF Export'),
      description: t('features.integrations.pdf.desc', 'Download as PDF format')
    }
  ];

  return (
    <LandingPageLayout currentPage="features">
      
      {/* Hero Section */}
      <section className="pt-24 pb-20 lux-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-6">
              {t('features.hero.title', 'Powerful Features for Modern Business')}
            </h1>
            <p className="text-lg text-gray-500 mb-10 max-w-3xl mx-auto leading-relaxed">
              {t('features.hero.subtitle', 'Everything you need to create, share, and manage your digital business presence in one place.')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/#pricing"
                className="lux-btn-gold inline-flex items-center justify-center px-8 py-3.5 text-base font-semibold rounded-full"
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = '/#pricing';
                }}
              >
                {t('features.hero.cta.pricing', 'View Pricing')}
              </Link>
              <Link
                to="/contact"
                className="lux-btn-outline inline-flex items-center justify-center px-8 py-3.5 text-base font-semibold rounded-full bg-white"
              >
                {t('features.hero.cta.contact', 'Contact Sales')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900 mb-4">
              {t('features.section.title', 'Comprehensive Feature Set')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('features.section.subtitle', 'All the tools you need to succeed in the digital age')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="lux-card p-8">
                <div className="lux-icon-tile mb-5">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold tracking-tight text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-500 mb-5 text-sm leading-relaxed">
                  {feature.description}
                </p>
                <ul className="space-y-2.5">
                  {feature.benefits.map((benefit, benefitIndex) => (
                    <li key={benefitIndex} className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="h-4 w-4 text-[#c8a45e] mr-2.5 flex-shrink-0" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="py-24 lux-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-900 mb-4">
              {t('features.integrations.title', 'Seamless Integrations')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('features.integrations.subtitle', 'Works with the tools you already use')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {integrations.map((integration, index) => (
              <div key={index} className="text-center">
                <div className="lux-icon-tile inline-flex mx-auto mb-4">
                  {integration.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {integration.name}
                </h3>
                <p className="text-gray-600">
                  {integration.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lux-dark-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4">
            {t('features.cta.title', 'Ready to Get Started?')}
          </h2>
          <p className="text-lg text-white/55 mb-10 leading-relaxed">
            {t('features.cta.subtitle', 'Join thousands of professionals who trust ScanVCF for their digital business needs.')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="lux-btn-gold inline-flex items-center justify-center px-9 py-4 text-base font-semibold rounded-full"
            >
              {t('features.cta.start', 'Start Free Trial')}
            </Link>
            <Link
              to="/#pricing"
              className="lux-btn-ghost inline-flex items-center justify-center px-9 py-4 text-base font-semibold rounded-full"
              onClick={(e) => {
                e.preventDefault();
                window.location.href = '/#pricing';
              }}
            >
              {t('features.cta.pricing', 'View Pricing')}
            </Link>
          </div>
        </div>
      </section>

    </LandingPageLayout>
  );
};

export default FeaturesPage;

